<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header">
            <div class="card-group-title">
                <h4 class="card-title">
                    {{ $t('org.dashboard.Monthly Cost') }}
                </h4>
                <h6 class="card-subtitle">
                    {{ $t('org.dashboard.12 month amount as of selected date') }}
                </h6>
            </div>
        </div>
        <div
            id="tour-edit-dashboard-step-4"
            :style="{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '24px',
                height: '24px',
                pointerEvents: 'none'
            }"
        />
        <BarChart
            class="dashboard-chart"
            :data="chartData"
            :options="chartOptions"
            :styles="responsiveStyle"
        />
    </b-overlay>
</template>
<script>
import CreateVueChart from '@/components/CreateVueChart.js'
import useSelectMonth from '@/composables/useSelectMonth'
import useCommonData from '@/composables/useCommonData'
import useDashboard from '@/composables/useDashboard'
import useXAxisData from '@/composables/useXAxisData'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { getCurrentInstance, computed, watch, inject, toRefs } from '@vue/composition-api'

export default {
    props: ['editMode'],
    components: {
        BarChart: CreateVueChart('Bar')
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { editMode } = toRefs(props)
        const theme = inject('theme')
        const $themeColors = inject('themeColors')
        const { t } = useI18nUtils()
        const { getSummaryMonths } = useDashboard()
        const { selectedMonth } = useSelectMonth()
        const { __toKFormat, __getMonthShortString, __getDollar } = useCommonData()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, data] = getSummaryMonths(currentParams.value)
        const { createLast12Months } = useXAxisData()
        const transparentize = (value, opacity) => {
            const r = parseInt(value.slice(1, 3), 16)
            const g = parseInt(value.slice(3, 5), 16)
            const b = parseInt(value.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${opacity})`
        }
        const breakPoint = computed(() => store.state['app']['windowWidth'])
        const responsiveStyle = {
            position: 'absolute',
            top: '62px',
            bottom: 0,
            left: '0',
            right: '0',
            'margin': '0rem 1.7rem 1.5rem 1.7rem'
        }
        const costMap = computed(() => {
            return data.value?.list.reduce((acc, cur) => {
                acc[cur.month] = cur.cost
                return acc
            }, {})
        })
        const labels = computed(() => createLast12Months(editMode.value ? '2024-05' : selectedMonth.value))
        const costs = computed(() => data.value?.list.length ? labels.value.map(month => costMap.value[month] || 0) : null)
        const backgrounColorArray = computed(() => labels.value.map(month => month === selectedMonth.value ? $themeColors.value.primary : transparentize($themeColors.value.primary, 0.3)))
        const chartData = computed(() => {
            return {
                labels: labels.value.map(month => __getMonthShortString(month.substring(5, 7))),
                datasets: [
                    {
                        type: 'bar',
                        data: costs.value,
                        barPercentage: 0.5,
                        maxBarThickness: 24,
                        backgroundColor: backgrounColorArray.value,
                        hoverBackgroundColor: backgrounColorArray.value
                    }
                ]
            }
        })

        const chartOptions = computed(() => ({
            tooltips: {
                intersect: false,
                callbacks: {
                    title: tooltipItem => tooltipItem[0].label,
                    label: tooltipItem => __getDollar(tooltipItem.value)
                }
            },
            cornerRadius: 3,
            layout: {
                padding: {
                    top: 30
                }
            },
            legend: {
                display: false
            },
            plugins: {
                noDataBackground: {
                    display: true,
                    imageSrc: require('@/assets/images/empty.png'),
                    text: t('org.dashboard.No data')
                },
                datalabels: {
                    color: theme.value === 'light' ? '#535353' : '#fff',
                    anchor: 'end',
                    align: 'end',
                    display: true,
                    font: function () {
                        let size = 0
                        if (breakPoint.value <= 480) {
                            size = 8
                        } else if (breakPoint.value <= 576) {
                            size = 13
                        } else {
                            size = 15
                        }
                        return { size }
                    },
                    offset: -2,
                    formatter: context => context === 0 ? '' : __toKFormat(context, 0)
                }
            },
            scales: {
                xAxes: [
                    {
                        display: true,
                        gridLines: {
                            color: '#dbdbdb',
                            display: false,
                            drawTicks: false,
                            zeroLineColor: 'rgba(200, 200, 200, 0.65)',
                            zeroLineWidth: 20
                        },
                        ticks: {
                            padding: 8
                        }
                    }
                ],
                yAxes: [
                    {
                        display: true,
                        gridLines: {
                            zeroLineColor: '#C8C8C8',
                            color: 'transparent',
                            drawTicks: false
                        },
                        ticks: {
                            callback: label => __toKFormat(label, 0),
                            beginAtZero: true,
                            autoSkip: false,
                            count: 6,
                            maxTicksLimit: 6,
                            padding: 10
                        }
                    }
                ]
            }
        }))
        watch(currentParams, (value, oldValue) => {
            getSummaryMonths(value)
        }, {
            deep: true
        })
        return {
            responsiveStyle,
            status,
            data,
            chartData,
            chartOptions
        }
    }
}
</script>

<style>

</style>
